import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import Button from 'components/Button/Button';
import { CarouselImgItem } from 'components/EmblaCarousel/EmblaCarouselForEdit/components/CarouselImgItem';
import CarouselVideoItem from 'components/EmblaCarousel/EmblaCarouselForEdit/components/CarouselVideoItem';
import { ThumbImgItem } from 'components/EmblaCarousel/EmblaCarouselForEdit/components/ThumbImgItem';
import ThumbVideoItem from 'components/EmblaCarousel/EmblaCarouselForEdit/components/ThumbVideoItem';
import { isHideButtonInCarousel } from 'components/EmblaCarousel/isHideButtonInCarousel';
import UploadVideo from './UploadVideo';
import { TLanguageIDs, ICustomGroups, TMediaFileType } from 'types';
import useEmblaCarousel from 'embla-carousel-react';
import {
	StyleSettingsCarouselUploaderContext,
	IStyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import Text from 'components/Text/Text';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import style from './Uploader.module.scss';

interface IUploader {
	onSetActualPageOfCarousel: (page: number) => void;
	group: ICustomGroups;
	languageId: null | TLanguageIDs;
	selectedIndex: number;
	selectedLanguage: TLanguageIDs | null;
	hasMediaFile: boolean;
	videoMaxFileSizeMB: number;
	deleteHandler: () => void;
	addItemHandler: (mediaFileType: TMediaFileType, sourceUrl: string, fileId: string) => void;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	isDisabledControls?: boolean;
}

const Uploader: React.FC<IUploader> = ({
	onSetActualPageOfCarousel,
	group,
	languageId,
	selectedIndex,
	selectedLanguage,
	hasMediaFile,
	videoMaxFileSizeMB,
	deleteHandler,
	addItemHandler,
	showGroupLoader,
	hideGroupLoader,
	isDisabledControls,
}) => {
	const { changeTypeOfModalWindow, setCropperSrc } = useContext<IStyleSettingsCarouselUploaderContext>(
		StyleSettingsCarouselUploaderContext,
	);
	const [prevItemLength, setPrevItemLength] = useState<number>(group.items.length);
	const [mainViewportRef, embla] = useEmblaCarousel({ skipSnaps: false });
	const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
		containScroll: 'keepSnaps',
		dragFree: true,
	});

	useEffect(() => {
		if (group.items.length) {
			if (group.items.length < prevItemLength) {
				embla?.scrollTo(0);
			}
			embla?.reInit();
			emblaThumbs?.reInit();
		}
		setPrevItemLength(group.items.length);
	}, [group.items]);

	const onThumbClick = useCallback(
		(index) => {
			if (!embla || !emblaThumbs) {
				return;
			}
			if (emblaThumbs.clickAllowed()) {
				embla.scrollTo(index);
			}
		},
		[embla, emblaThumbs],
	);

	const onSelect = useCallback(() => {
		if (!embla || !emblaThumbs) {
			return;
		}
		emblaThumbs.scrollTo(embla.selectedScrollSnap());
		onSetActualPageOfCarousel(embla.selectedScrollSnap());
	}, [embla, emblaThumbs]);

	useEffect(() => {
		if (!embla) {
			return;
		}
		onSelect();
		embla.on('select', onSelect);
	}, [embla, onSelect]);

	const deleteIconClasses = classNames(style.icon, style.deleteIcon);
	const deleteButtonClasses = classNames(style.deleteButton, {
		[style.buttonVisible]: hasMediaFile,
	});

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	const deletePhoto = () => {
		setCropperSrc(null);
		deleteHandler();
	};

	return (
		<div className={style.container}>
			{hasMediaFile ? (
				<>
					<div className={style.embla}>
						<div className={style.emblaViewport} ref={mainViewportRef}>
							<div className={style.emblaContainer}>
								{group.items
									.filter((el) => el.mediaFileType === 1 || el.mediaFileType === 2)
									.map((el) => (
										<div className={style.emblaSlide} key={el.translationId}>
											<div className={style.emblaSlideInner}>
												{el.mediaFileType === 1 ? (
													<CarouselImgItem
														filePath={el.filePath}
														translationId={el.translationId}
														sourceUrl={el.sourceUrl}
														isMakeImageRequest={selectedLanguage === languageId}
													/>
												) : null}
												{el.mediaFileType === 2 ? (
													<CarouselVideoItem
														filePath={el.filePath}
														translationId={el.translationId}
														sourceUrl={el.sourceUrl}
														isMakeImageRequest={selectedLanguage === languageId}
													/>
												) : null}
											</div>
										</div>
									))}
							</div>
						</div>
					</div>

					<div className={`${style.embla} ${style.emblaThumb}`}>
						<div className={style.emblaViewport} ref={thumbViewportRef}>
							<div className={style.emblaContainer}>
								{group.items
									.filter((el) => el.mediaFileType === 1 || el.mediaFileType === 2)
									.map((el, index: number) => (
										<div className={style.thumbWrapper} key={el.translationId}>
											{el.mediaFileType === 1 ? (
												<ThumbImgItem
													onClick={() => onThumbClick(index)}
													selected={index === selectedIndex}
													isMakeRequest={selectedLanguage === languageId}
													filePath={el.filePath}
													translationId={el.translationId}
													sourceUrl={el.sourceUrl}
												/>
											) : null}
											{el.mediaFileType === 2 ? (
												<ThumbVideoItem
													onClick={() => onThumbClick(index)}
													selected={index === selectedIndex}
													isMakeRequest={selectedLanguage === languageId}
													filePath={el.filePath}
													translationId={el.translationId}
													sourceUrl={el.sourceUrl}
												/>
											) : null}
										</div>
									))}
							</div>
						</div>
					</div>
				</>
			) : (
				<div className={style.noImageContainer}>
					<svg className={style.noImageIcon}>
						<use xlinkHref={`${breadcrumbs}#twoHills`} />
					</svg>
				</div>
			)}
			<div className={style.buttonsWrapper}>
				<div className={style.uploadButtons}>
					{!isHideButtonInCarousel(group, 1) && (
						<Button
							className={style.button}
							disabled={isDisabledControls}
							onClick={openCropper}
							data-test="uploadImageButton">
							<Text translationNameSpace="form" translationKey="uploadImageButton" asFragment={true} />
						</Button>
					)}
					{!isHideButtonInCarousel(group, 2) && (
						<UploadVideo
							onUploadItem={addItemHandler}
							IdForInput={group.id + languageId + 'video'}
							languageId={languageId}
							groupId={group.id}
							videoMaxFileSizeMB={videoMaxFileSizeMB}
							showGroupLoader={showGroupLoader}
							hideGroupLoader={hideGroupLoader}
							isDisabledControls={isDisabledControls}
						/>
					)}
					<div />
				</div>
				<Button
					className={deleteButtonClasses}
					theme="secondary"
					icon={
						<svg className={deleteIconClasses}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					}
					disabled={isDisabledControls}
					onClick={isDisabledControls ? undefined : deletePhoto}
					data-test="deleteButton">
					<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
				</Button>
			</div>
		</div>
	);
};

export default Uploader;
